import { createSlice } from "@reduxjs/toolkit";

/**
 * The order being built in the POS.
 *
 * paymentType and customerId live here rather than in CartPanel's local state
 * because they are part of the order: editing an existing invoice has to bring
 * back both, and clearing the cart has to drop both.
 */
const initialState = {
  cart: [],
  discount: 0,
  orderType: "dine-in",
  paymentType: "cash",
  customerId: "",
  editingOrderId: null,

  // Both exist for the kitchen ticket. The table number is how a runner finds
  // the food's destination, and the note is where "no onions" lives — the two
  // things a cook needs after the item list. Neither appears on the receipt.
  tableNumber: "",
  note: "",
};

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const item = state.cart.find(
        i => i.productId === action.payload.productId
      );

      if (item) {
        item.qty += 1;
      } else {
        state.cart.push({ ...action.payload, qty: 1 });
      }
    },

    increaseQty: (state, action) => {
      const item = state.cart.find(i => i.productId === action.payload);
      if (item) item.qty += 1;
    },

    decreaseQty: (state, action) => {
      const item = state.cart.find(i => i.productId === action.payload);
      if (item) {
        item.qty -= 1;
        if (item.qty === 0) {
          state.cart = state.cart.filter(
            i => i.productId !== action.payload
          );
        }
      }
    },

    setDiscount: (state, action) => {
      state.discount = Number(action.payload || 0);
    },

    setOrderType: (state, action) => {
      state.orderType = action.payload;
    },

    setPaymentType: (state, action) => {
      state.paymentType = action.payload;
      // Cash carries no customer. Leaving a stale id behind is how an order
      // gets billed to somebody who was picked and then unpicked.
      if (action.payload !== "invoice") state.customerId = "";
    },

    setCustomerId: (state, action) => {
      state.customerId = action.payload || "";
    },

    setTableNumber: (state, action) => {
      state.tableNumber = action.payload || "";
    },

    setNote: (state, action) => {
      state.note = action.payload || "";
    },

    // ✅ ADD THIS
    clearCart: (state) => {
      state.cart = [];
      state.discount = 0;
      state.orderType = "dine-in";
      state.paymentType = "cash";
      state.customerId = "";
      state.editingOrderId = null; // ✅ reset when clearing
      state.tableNumber = "";
      state.note = "";
    },

    loadOrderToCart: (state, action) => {
  const order = action.payload;

  // An order item stores no picture — the schema keeps menuItem, name,
  // quantity, price and subtotal, nothing else. So imageUrl is left empty
  // here on purpose and CartPanel looks the product up by its menuItem id
  // when it draws the row. That also keeps an edited order showing the
  // CURRENT picture rather than whatever it had months ago.
  state.cart = (order.items || []).map(i => ({
    productId: i.menuItem || i.productId, // handle your data shape
    name: i.name,
    qty: i.quantity || i.qty,
    price: i.price,
    imageUrl: "",
  }));
  state.discount = Number(order.discount || 0);
  state.orderType = order.orderType || "dine-in";
  state.paymentType = order.paymentType || "cash";
  // The order may arrive with customer populated or as a bare id.
  state.customerId = order.customer?._id || order.customer || "";
  state.editingOrderId = order._id || order.id; // ✅ store the order id
  state.tableNumber = order.tableNumber || "";
  state.note = order.note || "";
},

  },
});

export const {
  addToCart,
  increaseQty,
  decreaseQty,
  setDiscount,
  setOrderType,
  setPaymentType,
  setCustomerId,
  setTableNumber,
  setNote,
  clearCart, // ✅ EXPORT
  loadOrderToCart, // ✅ new
} = orderSlice.actions;

export default orderSlice.reducer;
